/* eslint-disable react/jsx-curly-brace-presence */
/* eslint-disable camelcase */
/* eslint-disable prefer-destructuring */
import React, { useRef, useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import parse from 'html-react-parser';
import TextsmsIcon from '@material-ui/icons/Textsms';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { ActionIcon } from '@mummssoftware/common-ui';
import {
  PNCounterIcon,
  isOrderAddendumType,
} from '@mummssoftware/global-context/PNCounter';
import {
  GlobalContext,
  OPEN_WIDGET,
} from '@mummssoftware/global-context/GlobalContext';
import { ProgressNotes } from '@mummssoftware/utils/constants';
import { checkTrue } from '@mummssoftware/utils/utils';
import moment from 'moment-timezone';

import {
  CellDiv,
  StyledSpan,
  StatusDotDiv,
  VoidableText,
} from '../../components/layout/WrapperDiv';
import { PrintSingleOrderCell } from '../Print/PrintSingleOrderCell';
import {
  getPhysician,
  getPerson,
  buildOrderAppendWidgetFilter,
  isOrderVoided,
} from '../utils/utils';
import defaults, {
  voided,
  voidedBy,
  voidedOn,
  voidedReason,
} from '../../translations/en.json';

const ButtonCell = styled.div``; // to append (VOIDED)

type VoidableCellProps = React.PropsWithChildren<{
  data: { voidpersonfirstname?: string | null };
}>;

const VoidableCell = ({ data, children }: VoidableCellProps) => (
  <CellDiv>
    <VoidableText $isVoided={isOrderVoided(data)}>{children}</VoidableText>
  </CellDiv>
);

export const OrderTypeCell = React.memo((props: any) => {
  const { data } = props && props;
  const tooltipRef = useRef<HTMLDivElement>(null);

  const useStyles = makeStyles(({ palette }: any) =>
    createStyles({
      tooltip: {
        backgroundColor: palette.background.tooltip,
        padding: '4px',
        margin: '0px',
        overflow: 'auto',
        maxHeight: '400px',
        color: palette.common.black,
      },
    }),
  );

  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current?.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // handle tooltip open/close.
  const handleTooltipToggle = () => {
    setOpen((prev) => !prev);
  };

  const voidPerson = `${data.voidpersonfirstname} ${data.voidpersonlastname} ${data.voidpersoncredentials}`;
  const reason = `${data.voidreason}`;
  const voidDate = moment(data.voiddatetime).format('MM/DD/YYYY'); // or 'YYYY-MM-DD', etc.

  const voidedStatusText = ` ${voidedBy} ${voidPerson} ${voidedOn} ${voidDate} ${voidedReason} ${reason}  `;

  const isVoided = isOrderVoided(data);
  const voidText = `${voided}`;
  return (
    <CellDiv
      ref={tooltipRef}
      onClick={isVoided ? handleTooltipToggle : undefined}
    >
      <VoidableText $isVoided={isVoided}>{data.ordertypename} </VoidableText>
      {isVoided ? (
        <Tooltip
          interactive
          onClose={() => setOpen(false)}
          open={open}
          disableHoverListener
          disableTouchListener
          title={<StyledSpan>{voidedStatusText}</StyledSpan>}
          classes={{
            tooltip: classes.tooltip,
          }}
        >
          <ButtonCell>{voidText}</ButtonCell>
        </Tooltip>
      ) : null}
    </CellDiv>
  );
});

// getorders can return null note / patientstandingordertext; parse() throws on non-strings.
const parseHtml = (html?: string | null) =>
  typeof html === 'string' && html ? parse(html) : null;

export const getOrderText = (order: mumms.Order) => {
  const hasPSOText =
    checkTrue(order.haspatientstandingorder) && order.patientstandingordertext;
  const psoText = hasPSOText ? parseHtml(order.patientstandingordertext) : null;
  const noteText = parseHtml(order.note);
  if (!psoText && !noteText) {
    return 'No Order text';
  }
  return (
    <>
      {psoText}
      {noteText}
    </>
  );
};

export const OrderTextCell = React.memo((props: any) => {
  const { data } = props && props;
  const useStyles = makeStyles(({ palette }: any) =>
    createStyles({
      tooltip: {
        backgroundColor: palette.background.tooltip,
        padding: '4px',
        margin: '0px',
        overflow: 'auto',
        maxHeight: '400px',
        color: palette.common.black,
      },
    }),
  );
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  return (
    <CellDiv>
      <Tooltip
        interactive
        onClose={handleTooltipClose}
        open={open}
        disableHoverListener
        disableTouchListener
        title={<StyledSpan>{getOrderText(data)}</StyledSpan>}
        classes={{
          tooltip: classes.tooltip,
        }}
      >
        <ActionIcon
          style={{
            margin: 0,
          }}
          Icon={TextsmsIcon}
          onClick={handleTooltipOpen}
        />
      </Tooltip>
    </CellDiv>
  );
});

export const PhysicianCell = React.memo((props: any) => {
  const { data } = props && props;

  return (
    <VoidableCell data={data}>
      {getPhysician(data)}
      {''}
    </VoidableCell>
  );
});

const formatShiftedDateTime = (shifted?: string | null) =>
  shifted ? moment.parseZone(shifted).format('YYYY-MM-DD hh:mm A') : '';
const formatShiftedDate = (shifted?: string | null) =>
  shifted ? moment.parseZone(shifted).format('YYYY-MM-DD') : '';

/** Order Date (communication) — date and time. */
export const OrderDateCell = React.memo((props: any) => {
  const { data } = props && props;
  // parseZone: keep wall-clock from shifted string; no browser/site TZ conversion.
  const dateTime = formatShiftedDate(data.communicationdatetimeshifted);

  return <VoidableCell data={data}>{dateTime}</VoidableCell>;
});

/** Order creation (noted) — date and time. */
export const OrderCreationCell = React.memo((props: any) => {
  const { data } = props && props;
  const shifted = data.noteddatetimeshifted;
  // parseZone: keep wall-clock from the string; no browser/site TZ conversion.
  const dateTime = formatShiftedDateTime(shifted);

  return <VoidableCell data={data}>{dateTime}</VoidableCell>;
});

export const CreatedByCell = React.memo((props: any) => {
  const { data } = props && props;

  return <VoidableCell data={data}>{getPerson(data)}</VoidableCell>;
});

export const PrintCell = React.memo((props: any) => {
  const { data } = props && props;

  return (
    <CellDiv>
      <PrintSingleOrderCell order={data} />
    </CellDiv>
  );
});

export const SignatureCell = (props: any) => {
  const complete = !!props.data.signaturedatetime;

  return (
    <CellDiv>
      <span>
        <Tooltip title={complete ? defaults.complete : defaults.incomplete}>
          <span>
            <StatusDotDiv complete={complete} />
          </span>
        </Tooltip>
      </span>
    </CellDiv>
  );
};

export const NoteCountCellRenderer = (props: any) => {
  const order = props?.data;
  const globalContext = useContext(GlobalContext);

  if (!order || !isOrderAddendumType(order.ordertypeshortname)) {
    return <CellDiv />;
  }

  const handleOpenAddendum = () => {
    if (globalContext && globalContext.dispatch) {
      globalContext.dispatch({
        type: OPEN_WIDGET,
        payload: {
          componentName: ProgressNotes,
          filter: buildOrderAppendWidgetFilter(order),
        },
      });
    }
  };

  return (
    <PNCounterIcon
      patientNumber={String(order.patientnumber)}
      count={order.ordernotecount ?? 0}
      openEntityWidget={false}
      onIconClick={handleOpenAddendum}
    />
  );
};
